/**
 * @unscoped — ChatPage 의 i18n — Cycle 61.
 *
 * 핵심 메시지 흐름 (ChatArea + Header + MessageInput + MessageList + MessageItem + ChatList)
 * 우선 + cycle 62 에 AI 카드 5 + HelpPage 분리. cycle 55-56 OnboardingWizard 2-cycle
 * cleanup 패턴 재적용.
 *
 * cycle 51 calendar-i18n / cycle 54 mail-i18n / cycle 55 onboarding-i18n /
 * cycle 57 board-i18n / cycle 58 task-i18n / cycle 59-60 settings-i18n 통합 패턴 일관.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== ChatArea (메인) =====
  'chat.area.aria_empty': { ko: '채팅 영역', en: 'Chat area', ja: 'チャットエリア' },
  'chat.area.aria_active_suffix': { ko: '채팅 영역', en: 'chat area', ja: 'チャットエリア' },
  'chat.area.empty_title': {
    ko: '대화를 선택하세요',
    en: 'Select a conversation',
    ja: '会話を選択してください',
  },
  'chat.area.empty_desc': {
    ko: '좌측 목록에서 대화방을 클릭하면 여기에 메시지가 표시됩니다.',
    en: 'Click a chat room from the left to display messages here.',
    ja: '左側のリストから会話を選択するとここにメッセージが表示されます。',
  },
  'chat.area.fallback_room_name': { ko: '대화', en: 'Conversation', ja: '会話' },
  // ===== v2.11 — 빈 상태 히어로 (대화 미선택) =====
  'chat.empty.hero_title': { ko: '대화를 시작해보세요', en: 'Start a conversation', ja: '会話を始めましょう' },
  'chat.empty.hero_desc': {
    ko: '왼쪽 목록에서 대화를 선택하거나, 새 대화 버튼으로 시작할 수 있어요.',
    en: 'Pick a conversation from the left, or start a new one with the new chat button.',
    ja: '左のリストから会話を選ぶか、新しい会話ボタンで始められます。',
  },
  'chat.empty.card_ai_title': { ko: 'AI와 대화', en: 'Chat with AI', ja: 'AIと会話' },
  'chat.empty.card_ai_desc': {
    ko: '일정·메일·태스크를 AI 비서에게 맡겨보세요',
    en: 'Let the AI assistant handle events, mail, and tasks',
    ja: '予定・メール・タスクをAIアシスタントに任せましょう',
  },
  'chat.empty.card_group_title': { ko: '그룹 대화', en: 'Group chat', ja: 'グループ会話' },
  'chat.empty.card_group_desc': {
    ko: '팀원들과 실시간으로 협업하세요',
    en: 'Collaborate with your team in real time',
    ja: 'チームとリアルタイムで協業しましょう',
  },
  'chat.empty.card_direct_title': { ko: '1:1 대화', en: 'Direct chat', ja: '1:1会話' },
  'chat.empty.card_direct_desc': {
    ko: '동료와 빠르고 가볍게 소통하세요',
    en: 'Reach a colleague quickly and casually',
    ja: '同僚と素早く気軽にやり取りしましょう',
  },
  'chat.area.sse_reconnecting': {
    ko: '실시간 연결 재시도 중…',
    en: 'Reconnecting in real-time…',
    ja: 'リアルタイム接続を再試行中...',
  },
  'chat.area.sse_exhausted': {
    ko: '실시간 연결이 끊어졌습니다. 새로고침하면 자동 복구됩니다.',
    en: 'Real-time connection lost. Refresh to recover automatically.',
    ja: 'リアルタイム接続が切断されました。リロードで自動復旧します。',
  },
  'chat.area.sse_unauthorized': {
    ko: '로그인이 만료되었습니다. 잠시 후 로그인 페이지로 이동합니다…',
    en: 'Session expired. Redirecting to login…',
    ja: 'セッションの有効期限が切れました。ログイン画面に移動します…',
  },
  'chat.area.member_load_failed': {
    ko: '멤버 목록 로드 실패: {msg}',
    en: 'Failed to load members: {msg}',
    ja: 'メンバー読み込み失敗: {msg}',
  },

  // ===== Header =====
  'chat.header.fallback_name': { ko: '이름 없음', en: 'No name', ja: '名前なし' },
  'chat.header.member_count_aria': {
    ko: '멤버 {n}명',
    en: 'Members: {n}',
    ja: 'メンバー {n}名',
  },
  'chat.header.member_count_text': { ko: '{n}명', en: '{n}', ja: '{n}名' },
  'chat.header.ai_present': {
    ko: 'AI 비서 참여 중',
    en: 'AI assistant present',
    ja: 'AIアシスタント参加中',
  },
  'chat.header.aria_voice_call': { ko: '음성 통화', en: 'Voice call', ja: '音声通話' },
  'chat.header.aria_video_call': { ko: '화상 통화', en: 'Video call', ja: 'ビデオ通話' },
  'chat.header.aria_more': { ko: '더보기', en: 'More', ja: 'もっと見る' },

  // ===== MessageInput =====
  'chat.input.aria_toolbar': { ko: '텍스트 서식', en: 'Text format', ja: 'テキスト書式' },
  'chat.input.aiMode.btnOn': {
    ko: 'AI 비서에게 질문하기',
    en: 'Ask AI assistant',
    ja: 'AIアシスタントに質問',
  },
  'chat.input.aiMode.btnOff': {
    ko: 'AI 모드 끄기 (일반 대화로 돌아가기)',
    en: 'Turn off AI mode (back to normal chat)',
    ja: 'AIモード解除',
  },
  'chat.input.aiMode.placeholder': {
    ko: '무엇이든 편하게 물어보세요',
    en: 'Ask anything…',
    ja: 'なんでも気軽に聞いてください',
  },
  'chat.input.aiMode.banner': {
    ko: 'AI 비서와 대화 중',
    en: 'Chatting with AI assistant',
    ja: 'AIアシスタントと会話中',
  },
  'chat.input.aiMode.bannerHint': {
    ko: '— 다른 사람에게는 전달되지 않아요',
    en: '— not sent to other members',
    ja: '— 他のメンバーには届きません',
  },
  'chat.input.placeholder': {
    ko: '메시지를 입력하세요. @AI 로 비서를 호출할 수 있어요',
    en: 'Type a message. Use @AI to summon the assistant',
    ja: 'メッセージを入力。@AI でアシスタントを呼び出せます',
  },
  'chat.input.aria_textarea': { ko: '메시지 입력', en: 'Message input', ja: 'メッセージ入力' },
  // 그림만 올리는 버튼이 아니다 — 파일 전반을 올린다(2026-08-26 이름 정정).
  'chat.input.aria_attach_file': { ko: '파일 첨부', en: 'Attach file', ja: 'ファイル添付' },
  'chat.input.aria_attach_emoji': { ko: '이모지', en: 'Emoji', ja: '絵文字' },
  'chat.input.aria_attach_voice': { ko: '음성', en: 'Voice', ja: '音声' },
  'chat.input.aria_send': { ko: '전송', en: 'Send', ja: '送信' },
  'chat.input.hint': {
    ko: 'AI 비서가 이 채팅의 모든 내용을 파악하고 있습니다 · @AI로 호출하세요',
    en: 'AI assistant is following this chat · summon with @AI',
    ja: 'AIアシスタントがこのチャットを把握しています · @AIで呼び出し',
  },
  // AI 답변 중 입력 잠금
  'chat.input.aiAnswering': { ko: 'AI가 답변 중입니다… 잠시만요', en: 'AI is answering…', ja: 'AIが回答中です…' },
  // 전송 오류 안내 (429/503 등)
  'chat.input.err.ai_rate': {
    ko: 'AI 요청이 많습니다 · {sec}초 후 다시 시도해 주세요',
    en: 'Too many AI requests · retry in {sec}s',
    ja: 'AIリクエストが多すぎます · {sec}秒後に再試行してください',
  },
  'chat.input.err.too_many': {
    ko: '연결이 많아 일시적으로 제한되었습니다 · 잠시 후 다시 시도해 주세요',
    en: 'Too many connections · try again shortly',
    ja: '接続が多すぎます · しばらくして再試行してください',
  },
  'chat.input.err.busy': {
    ko: '서버가 잠시 바쁩니다 · 잠시 후 다시 시도해 주세요',
    en: 'Server is busy · try again shortly',
    ja: 'サーバーが混雑しています · しばらくして再試行してください',
  },
  'chat.input.err.quota': {
    ko: '오늘 AI 사용 한도에 도달했습니다 · 내일 다시 시도하거나 관리자에게 문의하세요',
    en: 'Daily AI quota reached · try tomorrow or contact your admin',
    ja: '本日のAI利用上限に達しました · 明日再試行するか管理者へ',
  },
  'chat.input.err.send_failed': {
    ko: '전송에 실패했습니다 · 다시 시도해 주세요',
    en: 'Failed to send · please try again',
    ja: '送信に失敗しました · もう一度お試しください',
  },

  // ===== MessageList =====
  'chat.list.aria_log': { ko: '메시지 목록', en: 'Message log', ja: 'メッセージ一覧' },
  'chat.list.scroll_bottom_aria': { ko: '최신 메시지로 이동', en: 'Scroll to latest', ja: '最新メッセージへ移動' },
  'chat.list.empty': {
    ko: '아직 메시지가 없습니다. 첫 메시지를 보내보세요.',
    en: 'No messages yet. Send the first one.',
    ja: 'まだメッセージがありません。最初のメッセージを送信しましょう。',
  },
  'chat.list.day.today': { ko: '오늘 · {md}', en: 'Today · {md}', ja: '今日 · {md}' },
  'chat.list.day.yesterday': { ko: '어제 · {md}', en: 'Yesterday · {md}', ja: '昨日 · {md}' },
  'chat.list.day.md_ko': { ko: '{month}월 {day}일', en: '{month}/{day}', ja: '{month}月{day}日' },
  'chat.list.sender_unknown': { ko: '알 수 없음', en: 'Unknown', ja: '不明' },
  'chat.list.load_older': { ko: '이전 대화 불러오기', en: 'Load earlier messages', ja: '以前のメッセージを読み込む' },
  'chat.list.loading_older': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },

  // ===== 읽음/안읽음 (chat-read-receipts) =====
  'chat.read.divider': { ko: '여기부터 읽지 않았습니다', en: 'Unread messages', ja: 'ここから未読' },
  'chat.read.seen_direct': { ko: '읽음', en: 'Read', ja: '既読' },
  'chat.read.seen_group': { ko: '모두 읽음', en: 'Read by all', ja: '全員既読' },
  'chat.read.unread_count_aria': {
    ko: '{count}명이 아직 읽지 않음',
    en: '{count} have not read yet',
    ja: '{count}人が未読',
  },
  'chat.read.unread_one_aria': { ko: '상대가 아직 읽지 않음', en: 'Not read yet', ja: '相手が未読' },
  'chat.read.unread_names': { ko: '안 읽음: {names}', en: 'Unread: {names}', ja: '未読: {names}' },

  // ===== MessageItem =====
  'chat.item.ai_assistant': { ko: 'AI 비서', en: 'AI assistant', ja: 'AIアシスタント' },
  // 프로필 열기 (2026-08-31 · 시안 8/9) — 아바타·보낸이 이름을 누르면 사람 카드가 뜬다.
  'chat.item.open_profile': { ko: '{name} 프로필 보기', en: "View {name}'s profile", ja: '{name} のプロフィール' },
  'chat.item.system': { ko: '시스템', en: 'System', ja: 'システム' },
  'chat.item.system_badge_aria': { ko: '시스템 메시지', en: 'System message', ja: 'システムメッセージ' },
  'chat.item.ai_badge': { ko: 'AI', en: 'AI', ja: 'AI' },
  'chat.item.ai_prompt_label': {
    ko: 'AI 비서에게',
    en: 'To AI assistant',
    ja: 'AIアシスタントへ',
  },
  'chat.ai_thinking.text': {
    ko: '생각 중',
    en: 'Thinking',
    ja: '考え中',
  },
  'chat.ai_thinking.tool': {
    ko: '도구 사용 중: {tool}',
    en: 'Using tool: {tool}',
    ja: 'ツール使用中: {tool}',
  },
  'chat.ai_thinking.aria': {
    ko: 'AI 비서가 답변을 준비하고 있어요',
    en: 'AI assistant is preparing a response',
    ja: 'AIアシスタントが回答を準備中です',
  },
  'chat.item.aria_hover_toolbar': { ko: '메시지 액션', en: 'Message actions', ja: 'メッセージアクション' },
  'chat.item.aria_reply': { ko: '답장', en: 'Reply', ja: '返信' },
  'chat.item.aria_copy': { ko: '복사', en: 'Copy', ja: 'コピー' },
  'chat.item.aria_emoji': { ko: '이모지', en: 'Emoji', ja: '絵文字' },
  'chat.item.aria_more': { ko: '더보기', en: 'More', ja: 'もっと見る' },
  // chat-message-delete — 메시지 전체 회수.
  'chat.item.aria_delete': { ko: '메시지 삭제', en: 'Delete message', ja: 'メッセージを削除' },
  'chat.item.deleted_tombstone': { ko: '삭제된 메시지입니다', en: 'This message was deleted', ja: '削除されたメッセージです' },
  'chat.item.delete_confirm': {
    ko: '이 메시지를 삭제할까요? 모든 참여자에게 "삭제된 메시지입니다"로 표시됩니다.',
    en: 'Delete this message? It will show as "This message was deleted" for everyone.',
    ja: 'このメッセージを削除しますか？全員に「削除されたメッセージです」と表示されます。',
  },
  'chat.item.delete_failed': { ko: '메시지 삭제에 실패했습니다', en: 'Failed to delete message', ja: 'メッセージの削除に失敗しました' },

  // ===== ChatList =====
  'chat.sidebar.aria': { ko: '채팅 목록', en: 'Chat list', ja: 'チャット一覧' },
  'chat.sidebar.title': { ko: '채팅', en: 'Chats', ja: 'チャット' },
  'chat.sidebar.aria_new': { ko: '새 대화', en: 'New chat', ja: '新規チャット' },
  'chat.sidebar.search_placeholder': { ko: '대화 검색...', en: 'Search chats…', ja: 'チャット検索...' },
  'chat.sidebar.aria_search': { ko: '대화 검색', en: 'Search chats', ja: 'チャット検索' },
  'chat.sidebar.aria_filter_tablist': { ko: '채팅 필터', en: 'Chat filter', ja: 'チャットフィルター' },
  'chat.sidebar.aria_rooms_listbox': { ko: '대화방 목록', en: 'Chat rooms', ja: '会話一覧' },
  'chat.sidebar.filter.all': { ko: '전체', en: 'All', ja: 'すべて' },
  'chat.sidebar.filter.direct': { ko: '1:1', en: '1:1', ja: '1:1' },
  'chat.sidebar.filter.group': { ko: '그룹', en: 'Group', ja: 'グループ' },
  'chat.sidebar.filter.team': { ko: '팀', en: 'Team', ja: 'チーム' },
  'chat.sidebar.loading': {
    ko: '대화 목록 불러오는 중…',
    en: 'Loading chats…',
    ja: 'チャット一覧を読み込み中...',
  },
  'chat.sidebar.err_load': {
    ko: '대화 목록 로드 실패: {msg}',
    en: 'Failed to load chats: {msg}',
    ja: 'チャット一覧の読み込み失敗: {msg}',
  },
  'chat.sidebar.empty': { ko: '대화가 없습니다', en: 'No chats', ja: 'チャットがありません' },
  'chat.sidebar.room.fallback_name': { ko: '이름 없음', en: 'No name', ja: '名前なし' },
  'chat.sidebar.room.preview_recent': { ko: '최근 메시지', en: 'Recent message', ja: '最近のメッセージ' },
  'chat.sidebar.room.preview_empty': { ko: '대화 없음', en: 'No conversation', ja: '会話なし' },
  'chat.sidebar.room.member_count': {
    ko: '{n}명',
    en: '{n}',
    ja: '{n}人',
  },
  'chat.sidebar.room.menu_aria': { ko: '대화방 메뉴', en: 'Room menu', ja: 'チャットメニュー' },
  'chat.sidebar.room.leave': { ko: '대화방 나가기', en: 'Leave room', ja: 'チャットから退出' },
  'chat.sidebar.room.leaving': { ko: '나가는 중…', en: 'Leaving…', ja: '退出中...' },
  'chat.sidebar.room.leave_confirm': {
    ko: '이 대화방을 나가시겠어요? 다시 초대받기 전까지 메시지를 받을 수 없어요.',
    en: 'Leave this room? You won\'t receive messages until invited again.',
    ja: 'このチャットから退出しますか?再度招待されるまでメッセージを受け取れません。',
  },
  /* 판 「봇방」 — 업무자동화에 딸린 방은 나가면 **자동화까지 사라진다**. 종전 문구
     (「다시 초대받기 전까지…」)는 이 방엔 맞지 않는다 — 초대라는 것이 없다. */
  'chat.sidebar.room.leave_confirm_bot': {
    ko: '이 봇방을 나가면 연결된 업무자동화와 지금까지 받은 결과가 모두 삭제됩니다. 되돌릴 수 없습니다.',
    en: 'Leaving this bot room deletes the linked automation and all past results. This cannot be undone.',
    ja: 'このボットルームから退出すると、連携した業務自動化と過去の結果がすべて削除されます。元に戻せません。',
  },
  'chat.sidebar.room.leave_failed': {
    ko: '나가기 실패: {msg}',
    en: 'Leave failed: {msg}',
    ja: '退出失敗: {msg}',
  },

  // ===== Cycle 62 — AI 카드 5 components =====

  // ----- AiTextMessage -----
  'chat.ai_text.aria_hover_toolbar': {
    ko: 'AI 메시지 액션',
    en: 'AI message actions',
    ja: 'AIメッセージアクション',
  },
  'chat.ai_text.aria_retry': { ko: '다시 답변', en: 'Retry answer', ja: '再回答' },

  // ----- AiStreamingMessage -----
  'chat.ai_stream.aria_avatar_responding': {
    ko: 'AI 비서 (응답 중)',
    en: 'AI assistant (responding)',
    ja: 'AIアシスタント (応答中)',
  },
  'chat.ai_stream.aria_status': { ko: '응답 작성 중', en: 'Composing response', ja: '応答作成中' },
  'chat.ai_stream.status_text': { ko: '응답 중…', en: 'Responding…', ja: '応答中...' },

  // ----- AiActionCard (ScheduleQueryHint + UnknownActionPlaceholder) -----
  'chat.ai_action.schedule.aria': { ko: '일정 조회 응답', en: 'Schedule query response', ja: '予定照会応答' },
  'chat.ai_action.schedule.title': { ko: '일정 조회', en: 'Schedule query', ja: '予定照会' },
  'chat.ai_action.schedule.subtitle': {
    ko: '위 메시지의 일정 정보를 참고하세요',
    en: 'See the schedule info in the message above',
    ja: '上記メッセージの予定情報を参照してください',
  },
  'chat.ai_action.unknown.aria': { ko: 'AI 액션 카드', en: 'AI action card', ja: 'AIアクションカード' },
  'chat.ai_action.unknown.title': { ko: 'AI 액션 카드', en: 'AI action card', ja: 'AIアクションカード' },
  'chat.ai_action.unknown.subtitle': {
    ko: 'action_type 미상 — Phase 5 ai-gateway 응답 대기',
    en: 'action_type unknown — awaiting Phase 5 ai-gateway response',
    ja: 'action_type 不明 — Phase 5 ai-gateway 応答待ち',
  },

  // ----- MeetingSummaryCard -----
  'chat.meeting.aria': { ko: '회의 요약', en: 'Meeting summary', ja: '会議要約' },
  'chat.meeting.badge': { ko: '요약', en: 'Summary', ja: '要約' },
  'chat.meeting.label_attendees': { ko: '참석자', en: 'Attendees', ja: '出席者' },
  'chat.meeting.label_agenda': { ko: '안건', en: 'Agenda', ja: '議題' },
  'chat.meeting.label_key_decisions': { ko: '핵심 결정', en: 'Key decisions', ja: '主要決定' },
  'chat.meeting.label_action_items': { ko: '액션 아이템', en: 'Action items', ja: 'アクションアイテム' },
  'chat.meeting.fallback_title': { ko: '회의 요약', en: 'Meeting summary', ja: '会議要約' },
  'chat.meeting.fallback_subtitle': {
    ko: '요약 데이터 미도착 — Phase 5 ai-gateway 응답 대기',
    en: 'Summary data not arrived — awaiting Phase 5 ai-gateway response',
    ja: '要約データ未着 — Phase 5 ai-gateway 応答待ち',
  },

  // ----- AiManualCard -----
  'chat.manual.badge': { ko: '매뉴얼', en: 'Manual', ja: 'マニュアル' },
  'chat.manual.rows_aria': { ko: '재직기간별 표', en: 'Tenure-based table', ja: '在職期間別表' },
  'chat.manual.current_marker': { ko: ' ◀ 해당', en: ' ◀ matches', ja: ' ◀ 該当' },
  'chat.manual.user_desc_separator': { ko: '님 · ', en: ' · ', ja: 'さん · ' },
  'chat.manual.fallback_aria': { ko: 'AI 매뉴얼 조회', en: 'AI manual lookup', ja: 'AIマニュアル照会' },
  'chat.manual.fallback_title': { ko: 'AI 매뉴얼 조회', en: 'AI manual lookup', ja: 'AIマニュアル照会' },
  'chat.manual.fallback_subtitle': {
    ko: '매뉴얼 데이터 미도착 — Phase 5 ai-gateway 응답 대기',
    en: 'Manual data not arrived — awaiting Phase 5 ai-gateway response',
    ja: 'マニュアルデータ未着 — Phase 5 ai-gateway 応答待ち',
  },

  // ----- 우측 참가자 패널 (chat-participants — 2026-06-30) -----
  'chat.participants.title': { ko: '대화 상대', en: 'Participants', ja: '参加者' },
  'chat.participants.count': { ko: '{n}명', en: '{n}', ja: '{n}名' },
  'chat.participants.aria': { ko: '대화 참가자 목록', en: 'Conversation participants', ja: '会話の参加者一覧' },
  'chat.participants.ai_assistant': { ko: 'AI 비서', en: 'AI assistant', ja: 'AIアシスタント' },
  'chat.participants.ai_desc': { ko: '모든 대화에 참여', en: 'In every conversation', ja: 'すべての会話に参加' },
  'chat.participants.empty': { ko: '참가자 정보가 없습니다', en: 'No participant info', ja: '参加者情報がありません' },
  'chat.participants.role_owner': { ko: '방장', en: 'Owner', ja: '管理者' },
  'chat.participants.role_admin': { ko: '관리자', en: 'Admin', ja: '管理者' },
  'chat.participants.me_suffix': { ko: '(나)', en: '(me)', ja: '(自分)' },
  'chat.participants.invite_aria': { ko: '대화 상대 초대', en: 'Invite participants', ja: '参加者を招待' },

  // ----- 우측 사이드바 크기조절 + 미디어 (chat-sidebar-resize-media — 2026-07-01) -----
  'chat.sidebar.expand': { ko: '사이드바 펼치기', en: 'Expand sidebar', ja: 'サイドバーを展開' },
  'chat.sidebar.collapse': { ko: '사이드바 접기', en: 'Collapse sidebar', ja: 'サイドバーを折りたたむ' },
  'chat.sidebar.resize': { ko: '사이드바 폭 조절', en: 'Resize sidebar', ja: 'サイドバーの幅を調整' },
  'chat.sidebar.participants_title': { ko: '대화 상대', en: 'Participants', ja: '参加者' },
  'chat.media.images': { ko: '이미지', en: 'Images', ja: '画像' },
  'chat.media.files': { ko: '파일', en: 'Files', ja: 'ファイル' },
  'chat.media.section_aria': { ko: '대화 내 이미지·파일', en: 'Images and files in conversation', ja: '会話内の画像・ファイル' },
  'chat.media.empty': { ko: '아직 공유된 이미지나 파일이 없습니다', en: 'No shared images or files yet', ja: '共有された画像やファイルはまだありません' },
  'chat.media.image_open_aria': { ko: '이미지 열기: {name}', en: 'Open image: {name}', ja: '画像を開く: {name}' },
  'chat.media.download_aria': { ko: '{name} 다운로드', en: 'Download {name}', ja: '{name} をダウンロード' },
  'chat.media.preview_open_aria': {
    ko: '{name} 미리보기',
    en: 'Preview {name}',
    ja: '{name} をプレビュー',
  },

  // ----- 대화 초대 모달 (chat-room-invite — 2026-06-30) -----
  'chat.invite.title': { ko: '대화 상대 초대', en: 'Invite participants', ja: '参加者を招待' },
  'chat.invite.search_placeholder': { ko: '이름 또는 이메일로 검색', en: 'Search by name or email', ja: '名前またはメールで検索' },
  'chat.invite.searching': { ko: '검색 중…', en: 'Searching…', ja: '検索中…' },
  'chat.invite.no_results': { ko: '검색 결과가 없습니다', en: 'No results', ja: '検索結果がありません' },
  'chat.invite.already_member': { ko: '이미 참여 중', en: 'Already in', ja: '参加済み' },
  'chat.invite.selected_aria': { ko: '선택한 멤버', en: 'Selected members', ja: '選択したメンバー' },
  'chat.invite.results_aria': { ko: '검색 결과', en: 'Search results', ja: '検索結果' },
  'chat.invite.submit': { ko: '초대하기', en: 'Invite', ja: '招待する' },
  'chat.invite.submitting': { ko: '초대 중…', en: 'Inviting…', ja: '招待中…' },
  'chat.invite.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'chat.invite.failed': { ko: '초대에 실패했습니다', en: 'Failed to invite', ja: '招待に失敗しました' },
  'chat.invite.remove_aria': { ko: '{name} 제거', en: 'Remove {name}', ja: '{name} を削除' },
  // chat-invite-allow-admins (2026-08-11) — 초대 후보 행의 role 배지. 관리자 계정 구분 → 오초대 방지.
  'chat.invite.role_system_admin': { ko: '시스템 관리자', en: 'System admin', ja: 'システム管理者' },
  'chat.invite.role_admin': { ko: '관리자', en: 'Admin', ja: '管理者' },

  // ----- 우측 문서 미리보기 패널 (chat-doc-preview — 2026-06-30) -----
  'chat.preview.trigger': { ko: '미리보기', en: 'Preview', ja: 'プレビュー' },
  'chat.preview.trigger_aria': { ko: '{name} 미리보기', en: 'Preview {name}', ja: '{name} をプレビュー' },
  'chat.preview.title': { ko: '문서 미리보기', en: 'Document preview', ja: 'ドキュメントプレビュー' },
  'chat.preview.aria': { ko: '문서 미리보기 패널', en: 'Document preview panel', ja: 'ドキュメントプレビューパネル' },
  'chat.preview.close_aria': { ko: '미리보기 닫기', en: 'Close preview', ja: 'プレビューを閉じる' },
  'chat.preview.resize': { ko: '미리보기 폭 조절', en: 'Resize preview', ja: 'プレビューの幅を調整' },
  'chat.preview.fullscreen': { ko: '전체보기', en: 'Full view', ja: '全体表示' },
  'chat.preview.fullscreen_exit': { ko: '전체보기 해제', en: 'Exit full view', ja: '全体表示を解除' },
  // 한 줄 페이지 수 (chat-doc-preview-cols — 2026-08-11)
  'chat.preview.cols': { ko: '한 줄에 볼 페이지 수', en: 'Pages per row', ja: '1行あたりのページ数' },
  'chat.preview.cols_n': { ko: '{n}장씩 보기', en: '{n} per row', ja: '{n}枚ずつ表示' },
  // 확대/축소 + 썸네일 레일 (chat-doc-preview-rail — 2026-08-11)
  'chat.preview.zoom': { ko: '확대/축소', en: 'Zoom', ja: '拡大 / 縮小' },
  'chat.preview.zoom_in': { ko: '확대', en: 'Zoom in', ja: '拡大' },
  'chat.preview.zoom_out': { ko: '축소', en: 'Zoom out', ja: '縮小' },
  'chat.preview.zoom_reset': { ko: '화면에 맞춤 (100%)', en: 'Fit to screen (100%)', ja: '画面に合わせる (100%)' },
  'chat.preview.rail': { ko: '페이지 미리보기', en: 'Page thumbnails', ja: 'ページのサムネイル' },
  'chat.preview.rail_open': { ko: '페이지 미리보기 펼치기', en: 'Show thumbnails', ja: 'サムネイルを表示' },
  'chat.preview.rail_close': { ko: '페이지 미리보기 접기', en: 'Hide thumbnails', ja: 'サムネイルを隠す' },
  'chat.preview.rail_to_right': { ko: '미리보기를 오른쪽으로', en: 'Move thumbnails right', ja: 'サムネイルを右へ' },
  'chat.preview.rail_to_left': { ko: '미리보기를 왼쪽으로', en: 'Move thumbnails left', ja: 'サムネイルを左へ' },
  'chat.preview.go_to_page': { ko: '{n}쪽으로 이동', en: 'Go to page {n}', ja: '{n}ページへ移動' },
  'chat.preview.rail_nav': {
    ko: '페이지 목록 (위·아래 화살표로 이동)',
    en: 'Page list (use up/down arrows to move)',
    ja: 'ページ一覧 (上下の矢印キーで移動)',
  },
  'chat.preview.loading': { ko: '문서를 불러오는 중…', en: 'Loading document…', ja: 'ドキュメントを読み込み中…' },
  'chat.preview.error': {
    ko: '문서를 불러오지 못했습니다. (네트워크/CORS 제한일 수 있어요)',
    en: 'Failed to load the document. (May be a network/CORS restriction)',
    ja: 'ドキュメントを読み込めませんでした。(ネットワーク/CORS 制限の可能性)',
  },
  'chat.preview.open_source': { ko: '원본 열기', en: 'Open source', ja: '原本を開く' },
  'chat.preview.empty': { ko: '내용이 비어 있습니다.', en: 'The document is empty.', ja: '内容が空です。' },

  // ===== 미디어 브라우저 — 일자 그룹·아이콘 정렬·검색 (chat-media-date-groups — 2026-08-07) =====
  // 탭
  'chat.media.tabs_aria': { ko: '미디어 종류', en: 'Media type', ja: 'メディア種別' },
  'chat.media.tab_files': { ko: '파일', en: 'Files', ja: 'ファイル' },
  'chat.media.tab_images': { ko: '이미지', en: 'Images', ja: '画像' },
  // 검색
  'chat.media.search_placeholder_files': { ko: '파일명·보낸사람 검색', en: 'Search files or sender', ja: 'ファイル名・送信者を検索' },
  'chat.media.search_placeholder_images': { ko: '이미지명·보낸사람 검색', en: 'Search images or sender', ja: '画像名・送信者を検索' },
  'chat.media.search_aria': { ko: '첨부 검색', en: 'Search attachments', ja: '添付を検索' },
  'chat.media.search_clear': { ko: '검색어 지우기', en: 'Clear search', ja: '検索語を消去' },
  'chat.media.no_results': { ko: '검색 결과가 없습니다', en: 'No matching attachments', ja: '検索結果がありません' },
  // 정렬 (아이콘 버튼 — 툴팁/aria 가 유일한 의미 전달 경로)
  'chat.media.sort_aria': { ko: '정렬 기준', en: 'Sort by', ja: '並び替え' },
  'chat.media.sort.date_desc': { ko: '최신순 — 누르면 오래된순', en: 'Newest first — click for oldest', ja: '新しい順 — クリックで古い順' },
  'chat.media.sort.date_asc': { ko: '오래된순 — 누르면 최신순', en: 'Oldest first — click for newest', ja: '古い順 — クリックで新しい順' },
  'chat.media.sort.name_desc': { ko: '이름 역순 — 누르면 가나다순', en: 'Name Z→A — click for A→Z', ja: '名前の降順 — クリックで昇順' },
  'chat.media.sort.name_asc': { ko: '이름 가나다순 — 누르면 역순', en: 'Name A→Z — click for Z→A', ja: '名前の昇順 — クリックで降順' },
  'chat.media.sort.size_desc': { ko: '큰 파일순 — 누르면 작은 파일순', en: 'Largest first — click for smallest', ja: 'サイズの大きい順 — クリックで小さい順' },
  'chat.media.sort.size_asc': { ko: '작은 파일순 — 누르면 큰 파일순', en: 'Smallest first — click for largest', ja: 'サイズの小さい順 — クリックで大きい順' },
  'chat.media.sort_note': {
    ko: '이름·크기 정렬은 같은 날짜 안에서만 적용됩니다',
    en: 'Name and size sorting applies within each day',
    ja: '名前・サイズの並び替えは同じ日付の中でのみ適用されます',
  },
  'chat.media.expand_all': { ko: '모두 펼치기', en: 'Expand all', ja: 'すべて展開' },
  'chat.media.collapse_all': { ko: '모두 접기', en: 'Collapse all', ja: 'すべて折りたたむ' },
  // 종류 칩
  'chat.media.kind_aria': { ko: '파일 종류 필터', en: 'File type filter', ja: 'ファイル種別フィルター' },
  'chat.media.kind.all': { ko: '전체', en: 'All', ja: 'すべて' },
  'chat.media.kind.doc': { ko: '문서', en: 'Docs', ja: '文書' },
  'chat.media.kind.sheet': { ko: '표', en: 'Sheets', ja: '表' },
  'chat.media.kind.archive': { ko: '압축', en: 'Archives', ja: '圧縮' },
  'chat.media.kind.etc': { ko: '기타', en: 'Other', ja: 'その他' },
  // 일자 그룹
  'chat.media.day_ymd': { ko: '{year}년 {month}월 {day}일', en: '{year}/{month}/{day}', ja: '{year}年{month}月{day}日' },
  'chat.media.day_unknown': { ko: '날짜 미상', en: 'Unknown date', ja: '日付不明' },
  'chat.media.day_expand_aria': { ko: '{label} 펼치기', en: 'Expand {label}', ja: '{label} を展開' },
  'chat.media.day_collapse_aria': { ko: '{label} 접기', en: 'Collapse {label}', ja: '{label} を折りたたむ' },
  // 요약·빈 상태
  'chat.media.summary': { ko: '{count}개 · {size}', en: '{count} items · {size}', ja: '{count}件 · {size}' },
  'chat.media.tab_empty_files': { ko: '공유된 파일이 없습니다', en: 'No files shared yet', ja: '共有されたファイルはありません' },
  'chat.media.tab_empty_images': { ko: '공유된 이미지가 없습니다', en: 'No images shared yet', ja: '共有された画像はありません' },
  // 라이트박스
  'chat.media.lightbox_aria': { ko: '이미지 보기', en: 'Image viewer', ja: '画像ビューア' },
  'chat.media.lightbox_prev': { ko: '이전 이미지', en: 'Previous image', ja: '前の画像' },
  'chat.media.lightbox_next': { ko: '다음 이미지', en: 'Next image', ja: '次の画像' },
  'chat.media.lightbox_close': { ko: '닫기', en: 'Close', ja: '閉じる' },
  'chat.media.lightbox_position': { ko: '{index} / {total}', en: '{index} / {total}', ja: '{index} / {total}' },
  'chat.media.lightbox_failed': { ko: '이미지를 불러오지 못했습니다', en: 'Failed to load image', ja: '画像を読み込めませんでした' },
};

export type ChatI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 61 — ChatPage i18n lookup.
 * cycle 46 학습 phase 5 fallback chain (lang → en → ko) 적용.
 * params 치환 ({name}) — calendar / workspace / mail / onboarding / board / task / settings 와 일관.
 */
export function tChat(key: ChatI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getChatLang(): Lang { return getSharedLang(); }
export function setChatLang(value: Lang): void { setSharedLang(value); }
export function subscribeChatLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
